import { views } from '../../common/views';
import {
  fetchInsightsByDescription,
  submitInsightFeedback,
  fetchCommerceSwatResult,
  downloadInsight
} from './support-insights-api';
import {
  attachMarkdownExternalLinkAnalytics,
  createMarkdownRendererHTML
} from './markdown-renderer';
import { adobeDataLayerTrack } from '../../common/analytics';
import { getSelectedOrg } from './support-org-util';
import { getLabelV2 } from '../../common/labelV2.js';
import { appAssetsOrigin, getPathDetails } from '../../common/environments';

const TEST_NO_INSIGHTS = false;
const TEST_DELAY_MS = 1000; 
const SUPPORTED_PRODUCT_IDS = [
  'dx_commercecloud_support',
  'dx_commerceonprem_support',
  'dx_commercepaymentservices_support',
  'dx_commercereporting_support',
  'dx_commercemanagedservices_support'
];

export const isInsightsSupportedForProduct = (productId) => {
  if (!productId) return false;
  return SUPPORTED_PRODUCT_IDS.includes(productId.toLowerCase());
};

async function getCompanyLoginDetails() {
  const org = getSelectedOrg() || { name: '', id: '' };
  let companyEntitlements = '';
  try {
    // const profile = await getUserProfile();
    // const entitlements = profile?.data?.entitlements;
    // companyEntitlements = (Array.isArray(entitlements) ? entitlements : []).join(',');
    companyEntitlements = [].join(',');
  } catch (e) {
    /* ignore */
  }
  return {
    companyLoginName: org.name || '',
    companyLoginID: org.id || '',
    companyEntitlements,
  };
}

/** BCP 47 locale for `Intl` formatters (aligned with `labelV2` / URL language). */
function getInsightsIntlLocale() {
  const { lang } = getPathDetails();
  if (!lang || lang === 'en' || lang === 'zz') return 'en-US';
  const map = {
    'zh-Hans': 'zh-CN',
    'zh-Hant': 'zh-TW',
    'pt-BR': 'pt-BR',
    de: 'de-DE',
    es: 'es-ES',
    fr: 'fr-FR',
    it: 'it-IT',
    ja: 'ja-JP',
    ko: 'ko-KR',
    nl: 'nl-NL',
    sv: 'sv-SE',
  };
  return map[lang] || lang;
}

/**
 * Map API `impact` (e.g. HIGH, medium, Severe) to a localized label; CSS still uses the raw value lowercased.
 * @param {string | null | undefined} impact
 * @returns {string}
 */
function translateInsightImpact(impact) {
  const raw = String(impact ?? 'Severe').trim();
  if (!raw) return getLabelV2('Severe');
  const titleKey =
    raw === raw.toUpperCase() && raw.length > 1
      ? `${raw.charAt(0)}${raw.slice(1).toLowerCase()}`
      : `${raw.charAt(0).toUpperCase()}${raw.slice(1).toLowerCase()}`;
  const translated = getLabelV2(titleKey);
  return translated !== titleKey ? translated : raw;
}

/**
 * Human-readable relative time for insight `checkedAt` (ISO-8601).
 * Uses `Intl.RelativeTimeFormat` for locale-aware phrasing; older than 2 days: short localized date.
 * @param {string | null | undefined} checkedAt
 * @returns {string}
 */
function formatInsightCheckedAt(checkedAt) {
  if (!checkedAt) return '—';
  const parsed = Date.parse(checkedAt);
  if (Number.isNaN(parsed)) return '—';
  const locale = getInsightsIntlLocale();
  const diffMs = Date.now() - parsed;
  const dayMs = 24 * 60 * 60 * 1000;
  const dateOpts = { year: 'numeric', month: 'short', day: 'numeric' };

  if (diffMs < 0) {
    return new Date(parsed).toLocaleDateString(locale, dateOpts);
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const diffMins = Math.floor(diffMs / (60 * 1000));
  if (diffMins < 1) {
    const diffSec = Math.floor(diffMs / 1000);
    return rtf.format(diffSec < 1 ? 0 : -diffSec, 'second');
  }
  if (diffMins < 60) {
    return rtf.format(-diffMins, 'minute');
  }
  const diffHours = Math.floor(diffMs / (60 * 60 * 1000));
  if (diffHours < 24) {
    return rtf.format(-diffHours, 'hour');
  }
  const diffDays = Math.floor(diffMs / dayMs);
  if (diffDays <= 2) {
    return rtf.format(-diffDays, 'day');
  }
  return new Date(parsed).toLocaleDateString(locale, dateOpts);
}

let insightsData = [];

/**
 * Keeps the insights info tooltip aligned to its anchor while using fixed viewport positioning.
 */
function positionInsightTooltipFixed(tooltipWrapper, tooltipText) {
  if (!tooltipWrapper || !tooltipText) return;
  const rect = tooltipWrapper.getBoundingClientRect();
  const viewportPadding = 8;
  const spaceAbove = rect.top;
  const flipThreshold = 80;
  const showBelow = spaceAbove < flipThreshold;
  tooltipText.classList.toggle('info-tooltip-flipped-below', showBelow);
  tooltipText.style.position = 'fixed';
  tooltipText.style.zIndex = '10000';
  tooltipText.style.left = `${rect.left + rect.width / 2}px`;
  if (showBelow) {
    tooltipText.style.top = `${rect.bottom + 8}px`;
    tooltipText.style.transform = 'translate(-50%, 0)';
  } else {
    tooltipText.style.top = `${rect.top - 8}px`;
    tooltipText.style.transform = 'translate(-50%, -100%)';
  }
  tooltipText.style.visibility = 'visible';
  tooltipText.style.opacity = '1';

  const runAdjust = () => {
    const tooltipRect = tooltipText.getBoundingClientRect();
    let top = parseFloat(tooltipText.style.top);
    let left = parseFloat(tooltipText.style.left);
    if (showBelow) {
      const minAnchorTop = rect.bottom + 8;
      if (tooltipRect.top < viewportPadding) {
        tooltipText.style.top = `${minAnchorTop}px`;
      }
      const maxBottom = window.innerHeight - viewportPadding;
      if (tooltipRect.bottom > maxBottom) {
        const shiftedTop = top - (tooltipRect.bottom - maxBottom);
        tooltipText.style.top = `${Math.max(shiftedTop, minAnchorTop)}px`;
      }
    } else {
      const maxAnchorTop = rect.top - 8;
      if (tooltipRect.top < viewportPadding) {
        const adjustedTop = top + (viewportPadding - tooltipRect.top);
        tooltipText.style.top = `${Math.min(adjustedTop, maxAnchorTop)}px`;
      }
      const maxBottom = window.innerHeight - viewportPadding;
      if (tooltipRect.bottom > maxBottom) {
        const shiftedTop = top - (tooltipRect.bottom - maxBottom);
        tooltipText.style.top = `${Math.min(shiftedTop, maxAnchorTop)}px`;
      }
    }
    if (tooltipRect.left < viewportPadding) {
      tooltipText.style.left = `${left + (viewportPadding - tooltipRect.left)}px`;
    }
    const maxRight = window.innerWidth - viewportPadding;
    if (tooltipRect.right > maxRight) {
      left = parseFloat(tooltipText.style.left);
      tooltipText.style.left = `${left - (tooltipRect.right - maxRight)}px`;
    }
  };

  if (tooltipWrapper._insightTooltipAdjustRaf != null) {
    cancelAnimationFrame(tooltipWrapper._insightTooltipAdjustRaf);
  }
  tooltipWrapper._insightTooltipAdjustRaf = requestAnimationFrame(() => {
    tooltipWrapper._insightTooltipAdjustRaf = null;
    runAdjust();
  });
}

function attachInsightTooltipRepositionListeners(tooltipWrapper, handler, signal) {
  const opts = { passive: true, signal };
  window.addEventListener('scroll', handler, { ...opts, capture: true });
  window.addEventListener('resize', handler, opts);

  let node = tooltipWrapper;
  while (node) {
    if (node instanceof HTMLElement) {
      const st = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(st.overflowY) || /(auto|scroll|overlay)/.test(st.overflow)) {
        node.addEventListener('scroll', handler, opts);
      }
    }
    const parent = node.parentElement;
    if (parent) {
      node = parent;
      continue;
    }
    const root = node.getRootNode?.();
    if (root instanceof ShadowRoot) {
      node = root.host;
    } else {
      break;
    }
  }
}

export class SupportInsightsManager {
  constructor(shadowRoot) {
    this.root = shadowRoot;
    this.loaded = false;
    this.currentInsight = null;
    this.isSupported = false;
    this.el = { placeholder: shadowRoot.querySelector('#support-insights-placeholder') };
    this.escapeHandler = null;
    /** Cleared when org/product/project/description session changes (aligned with new-case form context). */
    this.insightsFeedbackSessionKey = null;
    /** Insight keys that already submitted feedback this session — reopening modal shows thanks. */
    this.submittedInsightFeedbackKeys = new Set();
  }

  $(selector) {
    return this.el.placeholder?.querySelector(selector);
  }

  $root(selector) {
    return this.root.querySelector(selector);
  }

  initElements() {
    this.el.container = this.$('#support-insights-container');
    this.el.list = this.$('#support-insights-list');
  }

  bindInsightTooltipWrapper(tooltipWrapper) {
    if (!tooltipWrapper) return;
    tooltipWrapper.addEventListener('mouseenter', async function() {
      const tooltipText = this.querySelector('.info-tooltip-text-insights');
      if (tooltipText) {
        this._insightTooltipScrollAbort?.abort();
        const ac = new AbortController();
        this._insightTooltipScrollAbort = ac;
        const reposition = () => positionInsightTooltipFixed(this, tooltipText);
        reposition();
        attachInsightTooltipRepositionListeners(this, reposition, ac.signal);
      }
      const companyLoginDetails = await getCompanyLoginDetails();
      adobeDataLayerTrack({
        "event": "linkClicked",
        "eventType": "web.webInteraction.linkclicks",
        companyLoginDetails,
        "link": {
          "linkTitle": 'Support Insights Tool tip',
          "linkLocation": 'Support Insights',
          "linkType": "tool tip",
        },
      });
    });
    tooltipWrapper.addEventListener('mouseleave', function() {
      this._insightTooltipScrollAbort?.abort();
      this._insightTooltipScrollAbort = null;
      if (this._insightTooltipAdjustRaf != null) {
        cancelAnimationFrame(this._insightTooltipAdjustRaf);
        this._insightTooltipAdjustRaf = null;
      }
      const tooltipText = this.querySelector('.info-tooltip-text-insights');
      if (tooltipText) {
        tooltipText.style.visibility = 'hidden';
        tooltipText.style.opacity = '0';
        tooltipText.style.position = '';
        tooltipText.style.left = '';
        tooltipText.style.top = '';
        tooltipText.style.transform = '';
        tooltipText.style.zIndex = '';
      }
    });
    this.$root('.support-insights-description a').addEventListener('click',async function () {
      void getCompanyLoginDetails().then((companyLoginDetails) => {
      adobeDataLayerTrack({
        "event": "linkClicked",
        "eventType": "web.webInteraction.linkclicks",
        companyLoginDetails,
        "link": {
          "linkTitle": 'Site Wide Analysis Tool',
          "linkLocation": 'Tool Tip',
          "linkType": "link",
        },
      });
    });      
    })
    
  }

  initTooltip() {
    this.el.placeholder?.querySelectorAll('.info-tooltip-wrapper-insights').forEach((w) => this.bindInsightTooltipWrapper(w));
  }

  loadTemplate() {
    const currentPlaceholder = this.root.querySelector('#support-insights-placeholder');

    if (this.loaded && this.el.placeholder !== currentPlaceholder) {
      this.loaded = false;
      this.el = { placeholder: null };
    }

    if (this.loaded) return true;

    this.el.placeholder = currentPlaceholder;
    if (!this.el.placeholder) return false;
    try {
      this.el.placeholder.innerHTML = views.get('support-insights')();
      this.loaded = true;
      this.initElements();
      this.initTooltip();
      return true;
    } catch (e) {
      console.error('Support Insights:', e);
      return false;
    }
  }

  initForProduct(productId) {
    const wrapper = this.root.querySelector('.recomend-container-wrapper');
    this.isSupported = isInsightsSupportedForProduct(productId);

    if (this.isSupported) {
      wrapper?.classList.add('insights-supported');
      wrapper?.classList.remove('insights-not-supported');
    } else {
      wrapper?.classList.add('insights-not-supported');
      wrapper?.classList.remove('insights-supported');
    }

    return this.isSupported;
  }

  async load({ orgId = '', productId = '', description = '', projectId = '', environment = 'production', email = '', onInsightsRecommended } = {}) {
    if (!this.initForProduct(productId)) return;

    const nextSessionKey = `${orgId}|${productId}|${projectId}|${(description || '').trim()}`;
    if (nextSessionKey !== this.insightsFeedbackSessionKey) {
      this.insightsFeedbackSessionKey = nextSessionKey;
      this.submittedInsightFeedbackKeys = new Set();
    }

    const wrapper = this.root.querySelector('.recomend-container-wrapper');
    const placeholder = this.root.querySelector('#support-insights-placeholder');
    const sectionReadyToHelp = this.root.querySelector('.support-insights-section .ready-to-help');
    const sectionLookingIntoIt = this.root.querySelector('.support-insights-section .looking-into-it');

    // Show loader and hide current content so only loader is visible during fetch/refetch
    sectionLookingIntoIt?.classList.remove('is-hidden');
    sectionReadyToHelp?.classList.add('is-hidden');
    this.el.container?.classList.add('is-hidden');
    placeholder?.querySelector('.support-insights-empty')?.classList.add('is-hidden');

    let data = TEST_NO_INSIGHTS ? [] : await fetchCommerceSwatResult({ projectId, environment, email, orgId });
    console.log('ecc data', data);
    
    if (data && data.length > 0) {
      onInsightsRecommended?.(data.length);
      if (!this.loadTemplate()) return;
      this.populate(data);
      wrapper?.classList.add('has-insights');
      wrapper?.classList.remove('no-insights');
      sectionReadyToHelp?.classList.add('is-hidden');
      sectionLookingIntoIt?.classList.add('is-hidden');
    } else {
      console.log('data 383', data);
      
      let errorMessage = 'No System Insights are currently available'
      if(data?.error){
        errorMessage = 'You do not have access to this project'
      }
      wrapper?.classList.remove('has-insights');
      wrapper?.classList.add('no-insights');
      const container = this.el.container ?? this.root.querySelector('#support-insights-container');
      container?.classList.add('is-hidden');
      sectionReadyToHelp?.classList.remove('is-hidden');
      sectionLookingIntoIt?.classList.add('is-hidden');
      // Show "no insights" message in the placeholder (replacing template DOM)
      if (placeholder) {
        placeholder.innerHTML = `
          <div class="support-insights-empty">
            <img
              src="${appAssetsOrigin}/assets/img/support/no-recommendations.svg"
              alt="no-recommendations"
              class="no-recommendations-img"
            />
            <div class="insight-heading">${getLabelV2(errorMessage)}</div>
            <p>${getLabelV2('Please continue logging your support case so our team can assist you directly.')}</p>
          </div>
        `;
      }
      // Reset so next load with data re-injects the template (placeholder was overwritten above)
      this.loaded = false;
      this.el.container = null;
      this.el.list = null;
    }
  }

  populate(data) {
    const { container, list } = this.el;
    if (!container || !list) return;

    insightsData = data;
    list.innerHTML = '';
    const session = this.insightsFeedbackSessionKey || '';
    data.forEach((insight, i) => {
      const fingerprint = [
        insight.title || '',
        insight.type || '',
        (insight.recommendation_compiled || insight.summary || insight.recommendation_template || '').slice(0, 64),
      ].join('\0') || `idx-${i}`;
      insight.__feedbackStorageKey = `${session}::${fingerprint}`;

      const item = this.createModalElement(views.get('support-insight-item')());

      if (item) {
        item.dataset.index = i;
        item.style.cursor = 'pointer';
        item.addEventListener('click', () => this.openModal(insightsData[i]));
      }

      const title = item?.querySelector('.support-insight-title');
      const text = item?.querySelector('.support-insight-text');
      const insightTitle = insight.title || 'Insight';

      // Use recommendation_template or extract text from recommendation_compiled
      let insightText = insight.recommendation_template || insight.summary || insight?.recommendation || '';
      if (!insightText && insight.recommendation_compiled) {
        // Strip HTML tags to get plain text for preview
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = insight.recommendation_compiled;
        //if the text is longer than 200 characters, truncate it and add ...
        console.log(tempDiv.textContent.trim(),'tempDiv.textContent.trim().length', tempDiv.textContent.trim().length);
        
        if (tempDiv.textContent.trim().length > 200) {
          insightText = tempDiv.textContent.trim().slice(0, 200) + '...';
        } else {
          insightText = tempDiv.textContent.trim();
        }
      }

      if (title) title.textContent = insightTitle;
      if (text) text.innerHTML = insightText;

      if (item) list.appendChild(item);
    });

    container.classList.remove('is-hidden');
    this.initExpandListener();
  }

  initExpandListener() {
    const expandBtn = this.$('#support-insights-expand-btn');
    if (expandBtn && !expandBtn.__init) {
      expandBtn.__init = true;
      expandBtn.addEventListener('click', () => this.toggleExpand());
    }
  }

  toggleExpand(forceState) {
    const wrapper = this.root.querySelector('.recomend-container-wrapper');
    if (!wrapper) return;

    const isCurrentlyExpanded = wrapper.classList.contains('is-expanded');
    const shouldExpand = forceState !== undefined ? forceState : !isCurrentlyExpanded;

    wrapper.classList.toggle('is-expanded', shouldExpand);
    void getCompanyLoginDetails().then((companyLoginDetails) => {
      adobeDataLayerTrack({
        "event": "linkClicked",
        "eventType": "web.webInteraction.linkclicks",
        companyLoginDetails,
        "link": {
          "linkTitle": 'Support Insights Collapse',
          "linkLocation": 'Support Insights',
          "linkType": "icon",
        },
      });
    });
  }

  createModalElement(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html.trim();
    return temp.firstElementChild;
  }

  openModal(insight) {
    if (this.$root('#support-insight-modal-overlay')) return;

    this.currentInsight = insight;
    const modal = this.createModalElement(views.get('support-insight-detail-modal')());
    this.root.appendChild(modal);
    console.log('insight', insight);
    this.fillModal(insight);
    this.attachModalListeners();
    document.body.style.overflow = 'hidden';

    void getCompanyLoginDetails().then((companyLoginDetails) => {
      adobeDataLayerTrack({
        "event": "linkClicked",
        "eventType": "web.webInteraction.linkclicks",
        companyLoginDetails,
        "link": {
          "linkTitle": insight.title + ' : ' + formatInsightCheckedAt(insight.checkedAt),
          "linkLocation": 'Support Insights',
          "linkType": "link",
        },
      });
    });
  }

  attachModalListeners() {
    const overlay = this.$root('#support-insight-modal-overlay');

    overlay?.querySelectorAll('.info-tooltip-wrapper-insights').forEach((w) => this.bindInsightTooltipWrapper(w));

    overlay?.addEventListener('click', (e) => {
      if (e.target.id === 'support-insight-modal-overlay') this.closeModal();
    });

    this.$root('#insight-modal-continue')?.addEventListener('click', () => {
      this.closeModal();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Continue Case Creation',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)} `,
            "linkType": "button",
          },
        });
      });
    });

    this.$root('#insight-modal-resolved')?.addEventListener('click', () => {
      this.closeModal();
      this.openResolvedModal();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Issue Resolved',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });

    this.$root('#insight-modal-download')?.addEventListener('click', () => {
      downloadInsight(this.currentInsight);
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Download',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
            "linkType": "download",
          },
        });
      });
    });

    this.escapeHandler = (e) => {
      if (e.key === 'Escape') this.closeModal();
    };
    document.addEventListener('keydown', this.escapeHandler);

    this.attachFeedbackListeners();
  }

  attachFeedbackListeners() {
    const feedbackForm = this.$root('#insight-feedback-form');
    const feedbackThanks = this.$root('#insight-feedback-thanks');
    const feedbackButtons = this.$root('#insight-modal-feedback-buttons');
    const feedbackUp = this.$root('#insight-feedback-up');
    const feedbackDown = this.$root('#insight-feedback-down');
    const feedbackSubmit = this.$root('#insight-feedback-submit');
    const feedbackDismiss = this.$root('#insight-feedback-dismiss');
    const feedbackTextarea = this.$root('#insight-feedback-textarea');

    let feedbackType = null;

    const showFeedbackForm = (type) => {
      feedbackType = type;
      if (feedbackForm) {
        feedbackForm.classList.remove('is-hidden');
        feedbackThanks?.classList.add('is-hidden');
        if (type === 'up') {
          feedbackUp?.classList.add('selected');
          feedbackDown?.classList.remove('selected');
        } else {
          feedbackDown?.classList.add('selected');
          feedbackUp?.classList.remove('selected');
        }
      }
    };

    const hideFeedbackForm = () => {
      if (feedbackForm) {
        feedbackForm.classList.add('is-hidden');
        feedbackUp?.classList.remove('selected');
        feedbackDown?.classList.remove('selected');
        if (feedbackTextarea) feedbackTextarea.value = '';
      }
    };

    const showThanksMessage = () => {
      hideFeedbackForm();
      feedbackButtons?.classList.add('is-hidden');
      feedbackThanks?.classList.remove('is-hidden');
    };

    const insightFeedbackKey = this.currentInsight?.__feedbackStorageKey;
    if (insightFeedbackKey && this.submittedInsightFeedbackKeys.has(insightFeedbackKey)) {
      showThanksMessage();
      return;
    }

    const setSubmittingState = (submitting) => {
      const submitLabel = feedbackSubmit?.getAttribute('data-submit-text') || 'Submit';
      const submittingLabel = feedbackSubmit?.getAttribute('data-submitting-text') || 'Submitting...';
      if (feedbackSubmit) {
        feedbackSubmit.disabled = submitting;
        feedbackSubmit.setAttribute('aria-busy', submitting ? 'true' : 'false');
        feedbackSubmit.textContent = submitting ? submittingLabel : submitLabel;
        submitting ? feedbackSubmit.classList.add('is-submitting') : feedbackSubmit.classList.remove('is-submitting');
      }
      if (feedbackDismiss) {
        feedbackDismiss.disabled = submitting;
      }
      if (!submitting) {
        updateSubmitDisabledFromTextarea();
      }
    };

    const resetSubmitState = () => {
      setSubmittingState(false);
    };

    const updateSubmitDisabledFromTextarea = () => {
      if (feedbackSubmit?.classList.contains('is-submitting')) return;
      const hasText = (feedbackTextarea?.value?.trim() || '').length > 0;
      if (feedbackSubmit) feedbackSubmit.disabled = !hasText;
    };

    feedbackUp?.addEventListener('click', () => {
      showFeedbackForm('up');
      updateSubmitDisabledFromTextarea();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Positive Feedback Button',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });
    feedbackDown?.addEventListener('click', () => {
      showFeedbackForm('down');
      updateSubmitDisabledFromTextarea();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Negative Feedback Button',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });
    feedbackTextarea?.addEventListener('input', updateSubmitDisabledFromTextarea);
    feedbackDismiss?.addEventListener('click', () => {
      if (!feedbackDismiss?.disabled) hideFeedbackForm();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Feedback Dismiss',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });
    feedbackSubmit?.addEventListener('click', async () => {
      if (feedbackSubmit?.disabled) return;
      if (insightFeedbackKey && this.submittedInsightFeedbackKeys.has(insightFeedbackKey)) {
        showThanksMessage();
        return;
      }
      const feedback = feedbackTextarea?.value || '';
      const projectUrlInput = this.root.querySelector('#commerce-project-url input');
      const projectUrl = projectUrlInput?.value?.trim() || '';
      setSubmittingState(true);
      try {
        const isFeedbackSubmitted = await submitInsightFeedback({
          type: feedbackType,
          feedback,
          insightTitle: this.currentInsight?.title,
          projectUrl,
          root: this.root
        });
        if (isFeedbackSubmitted) {
          if (insightFeedbackKey) this.submittedInsightFeedbackKeys.add(insightFeedbackKey);
          showThanksMessage();
          const companyLoginDetails = await getCompanyLoginDetails();
          adobeDataLayerTrack({
            "event": "linkClicked",
            "eventType": "web.webInteraction.linkclicks",
            companyLoginDetails,
            "link": {
              "linkTitle": 'Feedback Submit',
              "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight.checkedAt)}`,
              "linkType": "button",
            },
            "supportInsights": {
              "feedback": feedback,
            },
          });
        } else {
          console.error('error in submitting feedback');
          resetSubmitState();
        }
      } catch (err) {
        console.error('error in submitting feedback', err);
        resetSubmitState();
      }
    });
  }

  fillModal(d) {
    console.log('d', d);
    const set = (id, val, html = false) => {
      const el = this.$root(id);
      if (el) html ? (el.innerHTML = val || '') : (el.textContent = val || '');
    };
    const selectedButton = this.root.querySelector('#product-btn-container .product-btn-selected');
    const selectedProductName = selectedButton?.innerText?.trim();
    // Map API fields to UI fields
    const productName = selectedProductName || d.product_family_code || d.product_code || '';
    const riskLevel = d.impact ?? 'Severe';
    // const datapoint = d.datapoint.data ?? [];

    // Parse markdown-style recommendation to HTML
    // let recommendation = d.recommendation || d.recommendation_compiled || '';
    const datapoint = d?.data ?? [];
    // Parse markdown-style recommendation to HTML
    let recommendation = d.recommendation || d.recommendation_compiled || '';
    const insightCodeKey = recommendation.match(/\$\{([^}]+)\}/)?.[1];
    const hasHtmlFromApi = /<\s*(p|div|strong|em|ul|ol|table|br|h[1-6])\b/i.test(recommendation);
    const needsMarkdownRenderer =
      recommendation &&
      (recommendation.includes('##') ||
        recommendation.includes('${') ||
        hasHtmlFromApi);
    if (needsMarkdownRenderer) {
      recommendation = createMarkdownRendererHTML(recommendation, {
        datapoint,
        ...(insightCodeKey ? { insightCodeKey } : {}),
        allowDangerousHtml: true,
        variant: "body-sm",
        className: "recommendation-content",
        maxWidth: "100%",
        allowExternalLinks: true
      });
    }
    console.log('data to show in modal', {
      title: d.title,
      productName: productName,
      type: d.type || 'General',
      riskLevel: riskLevel,
      recommendation: recommendation,
      lastCheckRun: formatInsightCheckedAt(d.checkedAt)
    });
    set('#insight-modal-title', d.title);
    set('#insight-modal-product', productName);
    set('#insight-modal-type', d.type || 'General');
    set('#insight-modal-risk', translateInsightImpact(riskLevel));
    set('#insight-modal-last-check-label', getLabelV2('Last Check Run'));
    set('#insight-modal-last-check-run', formatInsightCheckedAt(d.checkedAt));
    set('#insight-modal-source-label', getLabelV2('Insight Source'));
    const sourceLink = this.$root('#insight-modal-source-link');
    if (sourceLink){
      sourceLink.textContent = getLabelV2('Site Wide Analysis Tool');
      sourceLink.addEventListener('click', async () => {
        void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": sourceLink.textContent,
            "linkLocation": `Support Insights: ${d?.title + ' : ' + formatInsightCheckedAt(d?.checkedAt)}`,
            "linkType": "link",
          },
        });
      });
      })
    } 
    set(
      '#insight-modal-source-tooltip',
      getLabelV2(
        'This insight comes from the Site-Wide Analysis Tool (SWAT). Open SWAT to see details for your Adobe Commerce setup.'
      )
    );
    const sourceInfoImg = this.$root('#insight-modal-source-info-img');
    if (sourceInfoImg) sourceInfoImg.setAttribute('alt', getLabelV2('Info icon'));
    set('#insight-modal-recommendation', recommendation, true);

    const recommendationEl = this.$root('#insight-modal-recommendation');
    if (recommendationEl) {
      void getCompanyLoginDetails().then(({ companyEntitlements }) => {
        attachMarkdownExternalLinkAnalytics(recommendationEl, {
          companyEntitlements,
          insightTitle: d.title,
        });
      });
    }

    const indicator = this.$root('#insight-modal-risk-indicator');
    if (indicator) {
      indicator.className = 'insight-risk-indicator';
      if (riskLevel) indicator.classList.add(riskLevel.toLowerCase());
    }
  }

  closeModal() {
    const modal = this.$root('#support-insight-modal-overlay');
    if (modal) {
      modal.remove();
    }
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }
    document.body.style.overflow = '';
  }

  openResolvedModal() {
    if (this.$root('#insight-resolved-modal-overlay')) return;

    const modal = this.createModalElement(views.get('support-insight-resolved-modal')());
    this.root.appendChild(modal);
    this.attachResolvedModalListeners();
    document.body.style.overflow = 'hidden';
  }

  attachResolvedModalListeners() {
    const overlay = this.$root('#insight-resolved-modal-overlay');

    overlay?.addEventListener('click', (e) => {
      if (e.target.id === 'insight-resolved-modal-overlay') this.cancelResolvedModal();
    });

    this.$root('#insight-resolved-cancel')?.addEventListener('click', () => {
      this.cancelResolvedModal();
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Cancel',
            "linkLocation": `Support Insights: ${this.currentInsight?.title + ' : ' + formatInsightCheckedAt(this.currentInsight?.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });

    this.$root('#insight-resolved-done')?.addEventListener('click', () => {
      const insightForTracking = this.currentInsight;
      this.closeResolvedModal();
      const myCasesTab = this.root.querySelector('[data-id="my-cases"]');
      if (myCasesTab) {
        myCasesTab.click();
      }
      void getCompanyLoginDetails().then((companyLoginDetails) => {
        adobeDataLayerTrack({
          "event": "linkClicked",
          "eventType": "web.webInteraction.linkclicks",
          companyLoginDetails,
          "link": {
            "linkTitle": 'Done',
            "linkLocation": `Support Insights: ${insightForTracking?.title + ' : ' + formatInsightCheckedAt(insightForTracking?.checkedAt)}`,
            "linkType": "button",
          },
        });
      });
    });
  }

  closeResolvedModal() {
    const modal = this.$root('#insight-resolved-modal-overlay');
    if (modal) {
      modal.remove();
    }
    document.body.style.overflow = '';
    this.currentInsight = null;
  }

  cancelResolvedModal() {
    const modal = this.$root('#insight-resolved-modal-overlay');
    if (modal) {
      modal.remove();
    }
    if (this.currentInsight) this.openModal(this.currentInsight);
  }

  hide() { this.el.container?.classList.add('is-hidden'); }
  show() { this.el.container?.classList.remove('is-hidden'); }

  showLoading() {
    const placeholder = this.root.querySelector('#support-insights-placeholder');
    if (placeholder && !this.loaded) {
      placeholder.innerHTML = `
        <div class="support-insights-loading">
          <div class="support-insights-loading-content">
            <div class="looking-into-it-heading-with-loader">
              <p class="heading-1">Looking into it<span class="looking-into-it-loading-dots"><span></span><span></span><span></span></span></p>
            </div>
            <p class="para">We're reviewing what you've shared to surface the most helpful steps.</p>
            <p class="heading-1">Feel free to keep filling out the form.</p>
          </div>
        </div>
      `;
    }
  }

  isLoaded() {
    return this.loaded;
  }

  reset() {
    const placeholder = this.root.querySelector('#support-insights-placeholder');
    if (placeholder) {
      placeholder.innerHTML = '';
    }
    this.loaded = false;
    this.currentInsight = null;
    insightsData = [];
    this.el = { placeholder: placeholder || null };
    this.insightsFeedbackSessionKey = null;
    this.submittedInsightFeedbackKeys = new Set();
  }
}

export const createSupportInsightsManager = (root) => new SupportInsightsManager(root);
export default SupportInsightsManager;
